import { Box, TableRow } from '@material-ui/core';
import { formatDateTimeReadable } from '@nemuru-eng/js-api-client';

import { NotificationStatusTag } from '@/components/modules/Notifications/components/NotificationStatusTag';
import { getNotificationValuesByChannel } from '@/components/modules/Notifications/constants/utils';
import { NotificationsTableRowProps } from '@/components/modules/Notifications/types/NotificationsProps';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { TableCellValue } from '@/components/shared/components/TableCellValue';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

export const NotificationTableRow = ({ notification, loan, tableProps }: NotificationsTableRowProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { isMobile } = useScreen();
  const { palette } = useCustomTheme();
  const tableValuesParams = getNotificationValuesByChannel({ notificationChannel: notification.channel, notification });

  const sentToNotificationRole = loan.onboardingDetails.find(
    (details) => details.id === notification.secondaryExternalId,
  )?.role;

  if (!tableValuesParams) return null;

  return (
    <>
      <TableRow key={notification.id}>
        <TableCellValue
          value={
            <StyledTooltip title={notification.providerName} tooltipProps={{ placement: 'top-end' }}>
              <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={8} justifyContent={'center'}>
                <Icon size={20} color={palette.text.title}>
                  {tableValuesParams.channelIconId}
                </Icon>
              </Box>
            </StyledTooltip>
          }
          tableCellProps={{
            style: {
              textAlign: 'center',
              ...(isMobile
                ? {
                    ...tableProps?.stickyCellStyle,
                    justifyContent: 'center',
                  }
                : {}),
            },
          }}
        />
        <TableCellValue
          value={
            <StyledTooltip title={tableValuesParams.sentTo} tooltipProps={{ placement: 'top-end' }}>
              <Box display={'flex'} flexDirection={'column'} width={'180px'}>
                <Font variant="body2" color="textPrimary" className="overflow-text-ellipsis">
                  {tableValuesParams.sentTo}
                </Font>
                <Font variant="caption" color="textSecondary" className="overflow-text-ellipsis">
                  <Text tId={sentToNotificationRole} />
                </Font>
              </Box>
            </StyledTooltip>
          }
        />
        <TableCellValue
          value={formatDateTimeReadable(notification.createdAt)}
          tableCellProps={{ className: 'overflow-text-ellipsis' }}
        />
        <TableCellValue value={<NotificationStatusTag status={notification.status} />} />
        <TableCellValue
          value={
            notification.deliveredAt ? (
              formatDateTimeReadable(notification.deliveredAt)
            ) : (
              <Box display={'flex'} alignItems={'center'} flexDirection={'row'} gridGap={6}>
                <Icon size={16} color="textSecondary">
                  schedule
                </Icon>
                {getTextIdTranslation('PENDING')}...
              </Box>
            )
          }
          tableCellProps={{ className: 'overflow-text-ellipsis' }}
        />
        <TableCellValue
          value={
            <StyledTooltip title={notification.message.subject} tooltipProps={{ placement: 'top-end' }}>
              <Box
                whiteSpace={'nowrap'}
                minWidth={'20vw'}
                maxWidth={'300px'}
                overflow={'hidden'}
                textOverflow={'ellipsis'}
              >
                {notification.message.subject}
              </Box>
            </StyledTooltip>
          }
        />
      </TableRow>
    </>
  );
};
